import { common, red } from '@mui/material/colors'
import { createTheme } from '@mui/material/styles'

const baseTheme = createTheme({
  spacing: (factor) => `${0.5 * factor}rem`,
  palette: {
    primary: {
      main: '#ff9e1b',
      contrastText: '#fff',
    },
    secondary: {
      main: common.white,
      contrastText: '#ff9e1b',
    },
    error: {
      main: red.A400,
    },
    background: {
      default: '#fafafa',
    },
    action: {
      hover: '#fffbf5',
    },
  },
  typography: {
    fontFamily: [
      'Open Sans',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
})

const theme = createTheme({
  spacing: baseTheme.spacing,
  palette: baseTheme.palette,
  typography: baseTheme.typography,
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#333333',
          fontSize: baseTheme.spacing(1.75),
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: baseTheme.spacing(2),
          },
        },
        h1: {
          fontWeight: '700',
          fontSize: baseTheme.spacing(5),
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: baseTheme.spacing(6),
          },
        },
        h2: {
          fontWeight: '700',
          fontSize: `${baseTheme.spacing(3.25)}`,
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: `${baseTheme.spacing(3.75)}`,
          },
        },
        h3: {
          fontWeight: '700',
          marginBottom: baseTheme.spacing(2),
          fontSize: baseTheme.spacing(2.5),
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: baseTheme.spacing(3),
          },
        },
        h4: {
          fontWeight: '700',
          fontSize: baseTheme.spacing(2.25),
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: baseTheme.spacing(2.5),
          },
        },
        h5: {
          fontSize: baseTheme.spacing(2),
          marginBottom: baseTheme.spacing(2),
          [baseTheme.breakpoints.up('md')]: {
            fontSize: baseTheme.spacing(2.4),
            marginBottom: baseTheme.spacing(2.4),
          },
        },
        h6: {
          fontWeight: '600',
          color: '#000000 !important',
          fontSize: baseTheme.spacing(2),
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: baseTheme.spacing(2.25),
          },
        },
        subtitle1: {
          color: '#9d9d9d',
          fontWeight: '600',
        },
        subtitle2: {
          color: '#333333',
          fontSize: baseTheme.spacing(1.75),
        },
        subtitle3: {
          color: '#a6a6a6',
          fontSize: baseTheme.spacing(1.35),
          display: 'block',
          [baseTheme.breakpoints.up('sm')]: {
            fontSize: baseTheme.spacing(1.6),
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: '600',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: baseTheme.spacing(2.8),
          boxShadow: 'none',
          padding: `${baseTheme.spacing(1)} ${baseTheme.spacing(4)}`,
          fontWeight: '600',
          marginTop: baseTheme.spacing(2),
          marginBottom: baseTheme.spacing(1),
          marginRight: baseTheme.spacing(2),
          textTransform: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          marginBottom: baseTheme.spacing(3),
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontWeight: '600',
        },
      },
    },
    MuiTablePagination: {
      defaultProps: {
        rowsPerPage: 10,
        rowsPerPageOptions: Array.of(10),
      },
      styleOverrides: {
        spacer: {
          flexGrow: 0,
          flexBasis: 0,
        },
        displayedRows: {
          flexGrow: 1,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          marginBottom: baseTheme.spacing(2),
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& > :nth-of-type(even)': {
            backgroundColor: '#fffbf5',
          },
          '& > :last-child td, & > :last-child th': {
            border: 0,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #f0dabc',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: baseTheme.spacing(1),
          whiteSpace: 'nowrap',
          paddingRight: baseTheme.spacing(2),
          '&:first-child': {
            paddingLeft: baseTheme.spacing(2),
          },
          '&:last-child': {
            paddingRight: baseTheme.spacing(2),
          },
          '& > .MuiFormControl-root': {
            margin: '0',
          },
          '& > button': {
            paddingTop: '0',
            paddingBottom: '0',
          },
        },
        head: {
          fontWeight: '700',
          fontSize: baseTheme.spacing(1.5),
          color: baseTheme.palette.black,
          backgroundColor: '#F1F1F1',
        },
        body: {
          fontSize: baseTheme.spacing(1.5),
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          flexDirection: 'row',
          fontWeight: '700',
          textTransform: 'none',
          '&>*:first-child': {
            width: baseTheme.spacing(3.5),
            height: baseTheme.spacing(3.5),
            margin: baseTheme.spacing(0, 1, 0, 0),
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          marginTop: baseTheme.spacing(3),
          marginBottom: '0',
        },
        li: {
          '& p, a': {
            fontSize: `${baseTheme.spacing(1.5)} !important`,
          },
          '&:first-child': {
            paddingBottom: baseTheme.spacing(0.35),
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          bottom: '0 !important',
          height: '100%',
          width: '100%',
          background: 'rgba(0,0,0,0.5)',
          left: '0px',
          right: '0px',
        },
      },
    },
  },
})

export default theme
